import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { PopoverBody, ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import {
  loadAllNotifications,
  changeSortConfigNotification,
  searchNotifications,
  setAll,
  setCollapseNotify,
  setCurrentNotification,
  setClearNotificationsList,
} from 'redux/actions/notificationActions';
import useApi from 'hooks/useApi';
import { addBreadcrumb } from 'redux/actions/layoutActions';

import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as SortByIcon } from 'assets/icon-sort-by.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import { ReactComponent as CheckRoundIcon } from 'assets/icon-check-round.svg';
import { ReactComponent as AngleArrowRightIcon } from 'assets/icon-angle-right.svg';
import { LabelIcon } from 'shared/components/Labels';
import { ButtonLabelIcon } from 'shared/components/Buttons';
import { colors } from 'shared/defaultTheme';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import AddNotify from 'shared/components/AddNotify';
import { AddFormModal } from 'shared/components/SharedStyles/SharedStyles';

import ClientsComponent from './components/Clients';
import ContactPanel from './components/ContactPanel';
import Table from './components/NotificationsTable';
import NotificationMassive from './components/NotificationMassive';
import {
  NotificationWrapper,
  NotificationPanelBackground,
  NotificationTableBackground,
  Popover,
  OptionMenu,
  SerachNotification,
  CollapseClientRight,
} from './styles';

const Notifications = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme);
  const { cusNavId, isCustomer, clientId, mail } = useSelector((state) => state.auth);
  const { mainColor } = theme;
  const notificationState = useSelector((state) => state.notification);
  const currentClient = useSelector((state) => state.clientState?.currentClient);
  const { sortConfig, searchValue, isCollapse, showTypeNotifications } = notificationState;
  const [orderName, setOrderName] = useState('Created');
  const [isOrder, setIsOrder] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);

  const [{ loading }, getNotificationsData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/notifications/${cusNavId}/search?login=${mail}`,
      method: 'POST',
    },
    { manual: true }
  );

  useEffect(() => {
    dispatch(
      addBreadcrumb({
        label: 'results_search_notifications_link',
        active: true,
        link: pathname,
        isModify: false,
      })
    );
  }, []);

  const getSortIcon = (fieldName) => {
    const sortOption = sortConfig.find(
      (currentSortOption) => currentSortOption.field === fieldName
    );
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const getOrderIcon = (isSort) => {
    const sortOption = sortConfig.find((currentSortOption) => currentSortOption.asc === isSort);
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const onSort = (fieldName, isSort) => {
    dispatch(changeSortConfigNotification({ fieldName, isSort }));
  };

  useEffect(() => {
    onSort(orderName, isOrder);
  }, [orderName, isOrder]);

  const notificationsData = async (searchParam = undefined) => {
    try {
      const findClientId = searchParam.filters.find((x) => x.field === 'ClientId');
      if (!findClientId.filter[0]) {
        dispatch(loadAllNotifications([]));
        dispatch(setAll([]));
        return;
      }
      const {
        data: { errors, data },
      } = await getNotificationsData(searchParam ? { data: searchParam } : undefined);
      if (errors.length) {
        return;
      }
      const today = new Date();
      const filterNotification = isCustomer
        ? data.filter((x) => x.id)
        : data.filter((x) => x.id && today >= new Date(x.date));
      dispatch(loadAllNotifications(filterNotification));
      dispatch(setAll(filterNotification));
    } catch (ex) {
      dispatch(loadAllNotifications([]));
      dispatch(setAll([]));
    }
  };

  useEffect(() => {
    return () => {
      dispatch(setAll([]));
    };
  }, []);

  const onChangeSearch = (e) => {
    if (e) {
      const { value } = e.target;
      dispatch(searchNotifications(value));
    } else {
      dispatch(searchNotifications(''));
    }
  };

  const searchNotification = () => {
    const sortOption = sortConfig.find((currentSortOption) => currentSortOption.asc !== 'none');
    const filters = sortOption
      ? [
          ...sortConfig,
          {
            model: 'Notify',
            field: 'ClientId',
            filter: [isCustomer ? currentClient?.clientId : clientId],
          },
          {
            model: 'Notify',
            field: 'Name',
            filter: searchValue && searchValue.length >= 2 ? [searchValue] : null,
            isRegex: !!(searchValue && searchValue.length >= 2),
          },
        ]
      : [
          {
            model: 'Notify',
            field: 'Id',
            asc: false,
          },
          {
            model: 'Notify',
            field: 'Name',
            filter: searchValue && searchValue.length >= 2 ? [searchValue] : null,
            isRegex: !!(searchValue && searchValue.length >= 2),
            asc: true,
          },
          {
            model: 'Notify',
            field: 'ClientId',
            filter: [isCustomer ? currentClient?.clientId : clientId],
          },
        ];
    if (!isCustomer) {
      filters.push({
        model: 'Notify',
        field: 'Mail',
        filter: [mail],
      });
    }
    const searchParam = {
      filters,
      reduce: {
        limit: 0,
        skip: 0,
      },
    };
    notificationsData(searchParam);
  };

  useEffect(() => {
    searchNotification();
  }, [sortConfig, currentClient]);

  useEffect(() => {
    if (searchValue != null && !loading) {
      searchNotification();
    }
  }, [searchValue]);

  const handleRefresh = () => {
    onSort('Created', false);
    dispatch(setClearNotificationsList(true));
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  return (
    <NotificationWrapper className="pb-3">
      {isCustomer ? (
        <>
          {!isCollapse ? (
            <NotificationPanelBackground className="mr-2">
              <ClientsComponent />
            </NotificationPanelBackground>
          ) : (
            <div className="collapse-client-container">
              <CollapseClientRight onClick={() => dispatch(setCollapseNotify(false))}>
                <AngleArrowRightIcon />
              </CollapseClientRight>
            </div>
          )}
        </>
      ) : (
        <>
          {!isCollapse ? (
            <NotificationPanelBackground className="mr-2">
              <ContactPanel />
            </NotificationPanelBackground>
          ) : (
            <div className="collapse-client-container">
              <CollapseClientRight onClick={() => dispatch(setCollapseNotify(false))}>
                <AngleArrowRightIcon />
              </CollapseClientRight>
            </div>
          )}
        </>
      )}
      <NotificationTableBackground className="mr-2" isCustomer={isCustomer} isCollapse={isCollapse}>
        <LabelIcon
          label={t('notifications_communication_title')}
          Icon={() => <ConversationIcon fill={colors.white} className="label-icon__notification" />}
          color={mainColor}
          className="ml-1 label-icon text-uppercase"
          classNameMain="mb-3"
        />
        {!showTypeNotifications && (
          <>
            <div className="d-flex justify-content-end action-header-mobile">
              <div className="form d-flex justify-content-end w-25 h-50 mr-4 w-container-search-mobile">
                <SerachNotification
                  Icon={() => null}
                  borderRadius={2}
                  className="mb-4 w-search-notification m-0 h-50"
                  inputClassName="w-100"
                  buttonClassName=""
                  rightIcon={<SearchIcon fill={colors.black} />}
                  onChange={onChangeSearch}
                  value={searchValue ?? ''}
                  placeholder={t('notification_search')}
                />
              </div>
              <div className="d-flex container-action-button">
                <ButtonLabelIcon
                  text={t('general_order_by')}
                  type="button"
                  Icon={() => <SortByIcon fill={colors.black} />}
                  className="button-icon mr-2"
                  id="PopoverFocusSort"
                />
                <ButtonLabelIcon
                  id="refresh-icon"
                  text={t('general_button_refresh')}
                  type="button"
                  Icon={() => <RefreshIcon fill={colors.black} />}
                  className="button-icon mr-2"
                  onClick={handleRefresh}
                />
                <ButtonLabelIcon
                  id="add-icon"
                  text={t('general_button_new')}
                  type="button"
                  Icon={() => <AddIcon fill={colors.black} />}
                  className="button-icon mr-2"
                  onClick={() => {
                    dispatch(setCurrentNotification(null));
                    setOpenFormModal(true);
                  }}
                />
              </div>
            </div>
            <Popover trigger="legacy" placement="bottom" target="PopoverFocusSort">
              <PopoverBody>
                <OptionMenu onClick={() => setOrderName('Name')}>
                  <LabelIcon
                    label={t('notification_column_issue')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getSortIcon('Name')}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setOrderName('Created')}>
                  <LabelIcon
                    label={t('notification_column_created_date')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getSortIcon('Created')}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setOrderName('Date')}>
                  <LabelIcon
                    label={t('notification_column_start_date')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getSortIcon('Date')}
                  />
                </OptionMenu>
                <hr />
                <OptionMenu onClick={() => setIsOrder(true)}>
                  <LabelIcon
                    label={t('general_ascending')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getOrderIcon(true)}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setIsOrder(false)}>
                  <LabelIcon
                    label={t('general_descending')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getOrderIcon(false)}
                  />
                </OptionMenu>
                <OptionMenu onClick={() => setIsOrder('none')}>
                  <LabelIcon
                    label={t('general_none')}
                    color=""
                    className="ml-1 body-text--primary action-option-order-label"
                    classNameMain="mb-3 label-icon-sort"
                    isStyle={false}
                    Icon={() => getOrderIcon('none')}
                  />
                </OptionMenu>
              </PopoverBody>
            </Popover>
            <Table
              handleRefresh={handleRefresh}
              notificationsData={notificationsData}
              loading={loading}
              setOpenFormModal={setOpenFormModal}
            />
          </>
        )}
        {showTypeNotifications && <NotificationMassive setOpenFormModal={setOpenFormModal} />}
      </NotificationTableBackground>
      <AddFormModal isOpen={openFormModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <div className="title-wrapper">
          <h1 className="title">{t('notification_title_add')}</h1>
        </div>
        <ModalBody>
          <AddNotify onCancel={onCloseFormModal} />
        </ModalBody>
      </AddFormModal>
    </NotificationWrapper>
  );
};

export default Notifications;
